// Meta (Facebook) Pixel integration for Meta Ads campaigns.
// Browser-side only (no Conversions API). Base code lives in public/index.html,
// this file wraps fbq() with the event helpers the funnel needs.

export const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '1083569531344885';
export const PIXEL_CURRENCY = 'INR';

const hasPixel = () => typeof window !== 'undefined' && typeof window.fbq === 'function';

export const isPixelEnabled = () => Boolean(META_PIXEL_ID) && hasPixel();

const clean = (params = {}) => {
  const out = {};
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === '') return;
    out[key] = value;
  });
  return out;
};

export const track = (eventName, params = {}) => {
  if (!isPixelEnabled()) return;
  try {
    window.fbq('track', eventName, clean(params));
  } catch (err) {
    console.warn('[MetaPixel] track failed:', err);
  }
};

// Standard events ---------------------------------------------------------

export const pageView = (custom = {}) => {
  if (!isPixelEnabled()) return;
  try {
    window.fbq('track', 'PageView', clean(custom));
  } catch (err) {
    console.warn('[MetaPixel] PageView failed:', err);
  }
};

export const viewContent = ({ content_ids, content_name, content_type = 'product', value, currency = PIXEL_CURRENCY, contents, ...rest } = {}) =>
  track('ViewContent', {
    content_ids,
    content_name,
    content_type,
    value,
    currency,
    contents,
    ...rest,
  });

export const search = (search_string, resultsCount) =>
  track('Search', { search_string, ...(resultsCount != null ? { results_count: resultsCount } : {}) });

export const addToCart = ({ content_ids, content_name, content_type = 'product', value, currency = PIXEL_CURRENCY, contents, num_items, ...rest } = {}) =>
  track('AddToCart', {
    content_ids,
    content_name,
    content_type,
    value,
    currency,
    contents,
    ...(num_items != null ? { num_items } : {}),
    ...rest,
  });

export const addToWishlist = ({ content_ids, content_name, content_type = 'product', value, currency = PIXEL_CURRENCY, ...rest } = {}) =>
  track('AddToWishlist', {
    content_ids,
    content_name,
    content_type,
    value,
    currency,
    ...rest,
  });

export const initiateCheckout = ({ content_ids, content_type = 'product', value, currency = PIXEL_CURRENCY, contents, num_items, ...rest } = {}) =>
  track('InitiateCheckout', {
    content_ids,
    content_type,
    value,
    currency,
    contents,
    ...(num_items != null ? { num_items } : {}),
    ...rest,
  });

export const addPaymentInfo = ({ content_ids, content_type = 'product', value, currency = PIXEL_CURRENCY, contents, ...rest } = {}) =>
  track('AddPaymentInfo', {
    content_ids,
    content_type,
    value,
    currency,
    contents,
    ...rest,
  });

export const purchase = ({ content_ids, content_type = 'product', value, currency = PIXEL_CURRENCY, contents, num_items, order_id, ...rest } = {}) =>
  track('Purchase', {
    content_ids,
    content_type,
    value,
    currency,
    contents,
    ...(num_items != null ? { num_items } : {}),
    ...(order_id ? { order_id } : {}),
    ...rest,
  });

export const completeRegistration = (custom = {}) => track('CompleteRegistration', custom);

export const lead = (custom = {}) => track('Lead', custom);

// Helpers -----------------------------------------------------------------

export const absoluteUrl = (path = '') => {
  if (typeof window === 'undefined') return path;
  const base = `${window.location.origin}${process.env.PUBLIC_URL || ''}`;
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
};

// Cart/order line items me `product` ya raw ObjectId hota hai ya populated object
// (backend `populate('items.product')` karta hai) - dono ko handle karna zaroori hai,
// warna content_ids me "[object Object]" chala jata hai.
const productIdOf = (item) => {
  const raw = item?.productId ?? item?.product ?? item?._id;
  if (!raw) return '';
  if (typeof raw === 'object') return String(raw._id || '');
  return String(raw);
};

// Standard e-commerce `contents` payload for one product.
export const productContents = (product) => {
  if (!product) return [];
  return [
    {
      id: String(product._id || product.id || ''),
      quantity: 1,
      item_price: Number(product.discountPrice || product.price || 0),
    },
  ];
};

// Standard e-commerce `contents` payload for a cart / order line-item list.
export const itemsContents = (items = []) =>
  items.map((item) => ({
    id: productIdOf(item),
    quantity: Number(item.quantity || 1),
    item_price: Number(item.discountPrice || item.price || 0),
  }));

export const idsFromItems = (items = []) =>
  items.map(productIdOf).filter(Boolean);
